import { pageTitle } from '@palaccount/config';
import { ADJUSTMENT_MESSAGES, type StockAdjustmentResponse } from '@palaccount/contracts';
import { Loader2, Plus, ShieldAlert, Trash2, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { Amount, Ltr, Qty } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { DocumentToolbar } from '@/components/document-toolbar';
import { ReasonBox } from '@/components/reason-box';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Typeahead } from '@/components/ui/typeahead';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';

import {
  captureBlocks,
  textFromStored,
  trackingPayload,
  type CaptureText,
  type TrackedItem,
} from '@/features/sales/tracking-capture';
import { TrackingRows } from '@/features/sales/tracking-rows';
import { useProjectPicker } from '@/features/projects/project-picker';

import { ItemCell } from './item-cell';
import { useItemPicker } from './item-picker';
import { multiplyQtyByFactor, trimZeros } from './conversion';
import { ApprovalChip, DirectionChip, VoucherStatusChip } from './stock-chips';
import {
  useStockAdjustment,
  useStockAdjustmentMutations,
  useWarehouses,
} from './stock-api';

/** BR-INV-08-01/-03 — the direction drives the reason list, both ways. */
const IN_REASONS = [
  'opening_balance',
  'found_stock',
  'production_output',
  'customer_return_non_invoice',
  'gift_received',
  'other',
] as const;
const OUT_REASONS = [
  'damaged_expired',
  'internal_use',
  'samples_marketing',
  'production_usage',
  'donation',
  'shrinkage_loss',
  /** PRJ-01: issued to a project — the Project field below names which. */
  'project_issue',
  'other',
] as const;

const QTY_RE = /^\d{1,12}(\.\d{1,3})?$/;
const COST_RE = /^\d{1,11}(\.\d{1,4})?$/;

interface LineRow {
  key: number;
  itemId: string;
  qty: string;
  unitCost: string;
  /**
   * `N-4`, as the SERVER wrote it for this line. The catalogue below is read
   * first — the picker can change an item under a row and the stored pair
   * would then describe the wrong thing — but a POSTED voucher may name an
   * item the active catalogue no longer lists, and a record that cannot say
   * "kg" is what this rule exists to stop.
   */
  unitSymbol: string;
  unitPrecision: number;
  /** BR-MD-03-17's capture, keyed exactly as the sales grids key it. */
  tracking: CaptureText;
}

let nextKey = 1;
const blankLine = (): LineRow => ({
  key: nextKey++,
  itemId: '',
  qty: '',
  unitCost: '',
  unitSymbol: '',
  unitPrecision: 0,
  tracking: {},
});

/** Exact 2dp sum of qty × cost — money never becomes a float. */
function sumLineValues(lines: LineRow[]): string {
  let cents = 0n;
  for (const line of lines) {
    if (!QTY_RE.test(line.qty.trim()) || !COST_RE.test(line.unitCost.trim())) continue;
    const value = multiplyQtyByFactor(line.qty.trim(), line.unitCost.trim(), 2);
    const [whole = '0', fraction = ''] = value.split('.');
    cents += BigInt(whole) * 100n + BigInt(`${fraction}00`.slice(0, 2));
  }
  return `${cents / 100n}.${(cents % 100n).toString().padStart(2, '0')}`;
}

function lineValue(line: LineRow): string | null {
  if (!QTY_RE.test(line.qty.trim()) || !COST_RE.test(line.unitCost.trim())) return null;
  return multiplyQtyByFactor(line.qty.trim(), line.unitCost.trim(), 2);
}

/**
 * A stored voucher back into the grid.
 *
 * BOTH figures are un-padded, and the cost is the one that mattered: the
 * column is `numeric(18,6)`, so a cost typed as `90.00` comes back as
 * `90.000000` — six decimals, where the WIRE CONTRACT (`CostStringSchema`)
 * and this screen's own `COST_RE` both allow four. Put back raw, it made the
 * editor refuse its own stored value: `validate()` returned the cost error,
 * so pressing Save on a voucher that had been saved once did nothing at all
 * and the request never left the browser. The line-value cell read "—" and
 * the grid footer read $0.00 beside a posting card reading the real total,
 * for the same reason.
 *
 * Trailing zeros are NUMERIC padding, not information (the phrase is
 * `trimZeros`'s own), and trimming cannot lose a real decimal because the
 * contract never let a fifth one in.
 */
function fromVoucher(voucher: StockAdjustmentResponse): LineRow[] {
  return voucher.lines.map((line) => ({
    key: nextKey++,
    itemId: line.itemId,
    qty: trimZeros(line.qty),
    unitCost: trimZeros(line.unitCost),
    unitSymbol: line.unitSymbol,
    unitPrecision: line.unitPrecision,
    tracking: textFromStored({ serialNos: line.serialNos, batchNo: line.batchNo }),
  }));
}

/**
 * INV-08 §5 — the Stock In / Stock Out voucher. Draft is freely editable and
 * moves NOTHING (BR-INV-08-18); posting allocates SI-/SO-#### and writes one
 * Adjustment± movement per line at the line's COST, never the selling price
 * (BR-INV-08-02/-09/-17). Approval, when the gate is on, blocks posting until
 * it clears — and a REJECTED voucher says so distinctly (BR-INV-08-07/-08).
 *
 * WHERE THE SERIALS ARE TYPED. The same sub-row the sales grids use, under
 * the line it belongs to, showing how many identifiers are still outstanding
 * (OQ-05-17's ruling, and the reason it rejected a dialog). INV-08 §5.4's
 * own field 14 already expected this shape — "Description … with variant and
 * batch sub-lines when present" — and reusing the component rather than
 * inventing a voucher-flavoured cell is what keeps one interaction for one
 * rule: a clerk who has filled in a delivery note has already filled in this.
 *
 * The picker hides a kit with auto-deduct ON, because such a kit is never
 * stocked as itself (MD-03 field 7) and the server refuses it by name.
 */
export function AdjustmentEditorPage() {
  // GEN-107: what these figures are IN, from the session rather than a
  // literal. Every one of them is a ledger or stock valuation, which the
  // company holds in its base currency; nothing here converts, so GEN-116
  // keeps the disclosure off the screen.
  //
  // ═══ ONE INV-08 RULE THIS CHOICE DOES NOT SATISFY — RULED, D29 ═══════════
  // Recorded with the scanner's ignore mark on the id's own line, so writing
  // this down does not count as citing it.
  //
  //   BR-INV-08-14 — rule-coverage:ignore — "the currency symbol on a line
  //     must follow the warehouse's default currency, not a hard-coded
  //     dollar sign." This was decision item 29's subject, verbatim, and
  //     delegated ruling D29 (26 Aug 2026) adopted exactly the option this
  //     note recommended: "stock voucher currency → base currency
  //     everywhere; BR-INV-08-14 recorded as deviation." The hard-coded      — rule-coverage:ignore — the ruling's own words spell the id a second time
  //     dollar half is met (the symbol above comes from the session, never
  //     a literal); the warehouse-currency half is retired WITH the ruling,
  //     because every figure on this voucher IS a base-currency figure —
  //     stock value is held in base and the §12 entry posts in base — so
  //     painting the warehouse's symbol beside it would put the wrong label
  //     on real money. The rule leaves the queue by a "Retired by delegated
  //     ruling D29" notice at its own declaration (lastmile--spec.patch;
  //     reversal condition: per-warehouse stock valuation landing), not by
  //     a citation here — the id stays ignore-marked because this screen
  //     enforces nothing of it. (Its neighbour BR-INV-08-10's zero-quantity
  //     sidestep is the separately-recorded confirm in the same decision
  //     item — see the validate() note below.)
  const baseCurrency = useBaseCurrency();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adjustmentId } = useParams<{ adjustmentId: string }>();
  const isNew = !adjustmentId;

  const { data: voucher, isPending: loading } = useStockAdjustment(adjustmentId);
  const { data: warehouses } = useWarehouses({ status: 'active' });

  const mutations = useStockAdjustmentMutations(adjustmentId ?? '');

  const canEdit = useCan(isNew ? 'inventory.create' : 'inventory.edit');

  const [direction, setDirection] = useState<'in' | 'out'>('in');
  const [adjustmentDate, setAdjustmentDate] = useDocumentDate();
  const [warehouseId, setWarehouseId] = useState('');
  const [binId, setBinId] = useState('');
  const [reason, setReason] = useState<string>('');
  const [requestedBy, setRequestedBy] = useState('');
  const [requireApproval, setRequireApproval] = useState(false);
  /**
   * PRJ-01 — the project a Stock OUT issues material to. '' = none. Only an
   * issue can name one, so the picker (and its search) exists only while the
   * direction is OUT, and flipping to IN clears it (the server does the same).
   */
  const [projectId, setProjectId] = useState('');
  /**
   * BR-INV-08-15 / ERR-INV-08-07 — a NEW voucher starts EMPTY, so there is
   * nothing to ask about.
   *
   * The prototype's New voucher kept the previous rows, warehouse, bin, reason
   * and requester under the next number, which made posting the same goods
   * twice a single click; the rule's answer was to add the question "Keep the
   * same lines for the next voucher?". This build removed the hazard instead
   * of guarding it: New is a route to `/inventory/adjustments/new`, which
   * mounts this component fresh with one blank line and every head field
   * unset, and there is no carry-forward path anywhere to prompt about.
   *
   * So the confirmation is ABSENT because what it protects against is absent —
   * not skipped. Anyone adding a "repeat this voucher" action owes the rule
   * its sentence at that moment, and this is where the debt is recorded.
   */
  const [lines, setLines] = useState<LineRow[]>([blankLine()]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /**
   * GEN-59 (spec line 3806): the cancel asks through §8's typed-reason
   * `ReasonBox` — "Cancel must always ask for a reason, and must store that
   * reason on the record." The contract carries it (stock-adjustments.ts
   * cancel body), the service writes `cancel_reason` — its OWN column,
   * distinct from the voucher's stock `reason` — plus the audit-row reason
   * (20260903100000), and the cancelled notice reads it back. The undone
   * clause is in the prompt because cancelled is TERMINAL, established in
   * the service: `notADraft` 409 on every further transition, no un-cancel
   * route on the contract — the correction is a fresh voucher.
   */
  const [cancelAsk, setCancelAsk] = useState(false);
  const loaded = useRef<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t(isNew ? 'adj.newTitle' : 'adj.editTitle'));
  }, [t, isNew]);

  useEffect(() => {
    if (!voucher) return;
    const stamp = `${voucher.id}:${voucher.updatedAt}:${voucher.status}`;
    if (loaded.current === stamp) return;
    loaded.current = stamp;
    setDirection(voucher.direction);
    setAdjustmentDate(voucher.adjustmentDate);
    setWarehouseId(voucher.warehouseId);
    setBinId(voucher.binId ?? '');
    setReason(voucher.reason);
    setRequestedBy(voucher.requestedBy);
    setRequireApproval(voucher.requireApproval);
    setProjectId(voucher.projectId ?? '');
    setLines(voucher.lines.length > 0 ? fromVoucher(voucher) : [blankLine()]);
    setError(null);
  }, [voucher]);

  const status = voucher?.status ?? 'draft';
  const isDraft = status === 'draft';
  const editable = isDraft && canEdit;

  const warehouse = (warehouses ?? []).find((row) => row.id === warehouseId);
  const showBins = Boolean(warehouse?.useBins) && (warehouse?.bins.length ?? 0) > 0;

  const reasons = direction === 'in' ? IN_REASONS : OUT_REASONS;
  // GEN-50: the project is searched, not listed — and not searched at all on
  // an inbound voucher, which has no project field.
  const projectPicker = useProjectPicker(projectId === '' ? null : projectId, direction === 'out');
  /*
   * GEN-50 — searched, not listed, and narrowed by the SERVER: BR-MD-03-07
   * and MD-03 field 7 (an auto-deducting kit is assembled when it is sold and
   * never sits on a shelf, so a voucher has no quantity of it to move) are
   * the `stockable` predicate. Folding that in the browser would have cut a
   * page of twenty down to whatever survived it.
   */
  const itemPicker = useItemPicker(
    useMemo(() => lines.map((line) => line.itemId), [lines]),
    { status: 'active', stockable: true },
  );
  const items = useMemo(() => [...itemPicker.byId.values()], [itemPicker.byId]);
  const itemsById = useMemo(
    () => new Map(items.map((item) => [item.id, item as TrackedItem])),
    [items],
  );

  /**
   * `N-4` — the unit this row's quantity is measured in. A voucher line has
   * no unit of its own (`AdjustmentLineInputSchema` is item + quantity +
   * cost), so it is always the item's STOCK unit and `BR-MD-03-04d`'s
   * line-versus-stock distinction cannot arise here.
   */
  const unitFor = (line: LineRow) => {
    const item = itemPicker.byId.get(line.itemId);
    return item
      ? { symbol: item.stockUnitSymbol, precision: item.stockUnitPrecision }
      : { symbol: line.unitSymbol, precision: line.unitPrecision };
  };

  const usedLines = lines.filter((line) => line.itemId !== '' && line.qty.trim() !== '');
  const totalValue = sumLineValues(usedLines);

  /**
   * Switching direction invalidates the reason — the two lists never overlap.
   *
   * ERR-INV-08-04 is this, and it is the rare §14 row describing behaviour
   * that is CORRECT: the reason resetting is what the user should see, because
   * `Found Stock` is not a thing a Stock Out can mean. Blanking it here rather
   * than picking a new default makes the empty field ask the question, and
   * `validate()` below refuses to save without an answer. The server refuses
   * the mismatched pair independently (BR-INV-08-03), so a stale reason cannot
   * arrive by any other route either.
   */
  const onDirection = (next: 'in' | 'out') => {
    if (!editable || next === direction) return;
    setDirection(next);
    setReason('');
    // PRJ-01: stock does not arrive FROM a project through this door.
    if (next === 'in') setProjectId('');
  };

  /**
   * ERR-INV-08-05 / BR-INV-08-05 — the bin belongs to the location, so moving
   * the location cannot keep it. Cleared here AND re-cleared by the server on
   * a PATCH that names a new warehouse without naming a bin, which is what
   * stops a bin surviving through a route that never touched this screen.
   */
  const onWarehouse = (next: string) => {
    setWarehouseId(next);
    setBinId('');
  };

  const setLine = (key: number, patch: Partial<LineRow>) =>
    setLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    );

  const setTracking = (key: number, blockKey: string, value: string) =>
    setLines((current) =>
      current.map((line) =>
        line.key === key ? { ...line, tracking: { ...line.tracking, [blockKey]: value } } : line,
      ),
    );

  const handleFailure = (
    thrown: unknown,
    permission: 'create' | 'edit' | 'delete' | 'approve',
  ) => {
    if (thrown instanceof ApiError && thrown.status === 403) {
      denyPermission(`inventory.${permission}`);
    }
    // D46: the cause rides along — this door raises BOTH converted families:
    // the stock refusals (serviceNeverStocked, itemInactive, serial/batch
    // presence, insufficientStock) and the warehouse wall (wh.errClosed at
    // the adjustment validator), all carrying key+params for the Arabic
    // reader.
    setError(
      localizeServerMessage(t, thrown instanceof Error ? thrown.message : String(thrown), thrown),
    );
  };

  const validate = (): string | null => {
    if (!warehouseId) return t('adj.errWarehouse');
    if (!reason) return t('adj.errReason');
    // PRJ-01: a project issue is meaningless without the project it issues
    // to — the contract refuses the pair too; this is the sentence in front.
    if (reason === 'project_issue' && projectId === '') return t('adj.errProject');
    if (requestedBy.trim() === '') return t('adj.errRequestedBy');
    if (usedLines.length === 0) return localizeServerMessage(t, ADJUSTMENT_MESSAGES.zeroTotal);
    for (const line of usedLines) {
      /*
       * BR-INV-08-10 — AND THE BEHAVIOUR HERE IS NOT THE RULE'S WORDING, so
       * read this before "fixing" either one.
       *
       * The rule says a line of zero or less is "skipped without comment".
       * This build cannot reach that state: a quantity of zero is refused at
       * the door, here and again by `QtyStringSchema` on the wire, which has
       * no sign and no all-zero form. So no ≤0 line is ever stored, none is
       * ever posted, and nothing is ever skipped — the rule's OUTCOME (such a
       * line never becomes a movement) is kept strictly, while its MECHANISM
       * (silent skipping) is absent by construction.
       *
       * The spec is not of one mind about it either: §15's Zero row says "a
       * zero line is skipped silently on posting. It should say so", and §16's
       * definition of done says "Zero lines are skipped and the user is told
       * how many were skipped" — which is the opposite of "without comment".
       * Refusing is the only reading that satisfies all three, and it is the
       * one a person can act on. Recorded for the owner rather than resolved.
       */
      if (!QTY_RE.test(line.qty.trim()) || /^0+(\.0+)?$/.test(line.qty.trim())) {
        return t('adj.errQty');
      }
      if (line.unitCost.trim() !== '' && !COST_RE.test(line.unitCost.trim())) {
        return t('adj.errCost');
      }
    }
    return null;
  };

  /**
   * A voucher line is one thing that moves, so only the line's OWN block
   * travels: `components` belongs to a kit that expands, and such a kit
   * cannot be on a voucher at all.
   */
  const lineTracking = (line: LineRow) => {
    const payload = trackingPayload(
      captureBlocks(itemsById.get(line.itemId), line.qty.trim()),
      line.tracking,
    );
    return {
      ...(payload.serialNos ? { serialNos: payload.serialNos } : {}),
      ...(payload.batchNo ? { batchNo: payload.batchNo } : {}),
    };
  };

  const requestBody = () => ({
    direction,
    adjustmentDate,
    warehouseId,
    binId: showBins && binId !== '' ? binId : null,
    reason: reason as (typeof IN_REASONS)[number] | (typeof OUT_REASONS)[number],
    requestedBy: requestedBy.trim(),
    requireApproval,
    // PRJ-01: sent explicitly on every save, so clearing the field on a draft
    // actually clears it (absent would leave the stored link in place).
    projectId: direction === 'out' && projectId !== '' ? projectId : null,
    lines: usedLines.map((line) => ({
      itemId: line.itemId,
      qty: line.qty.trim(),
      ...(line.unitCost.trim() === '' ? {} : { unitCost: line.unitCost.trim() }),
      // Half-filled blocks are sent as they stand rather than dropped — the
      // server's refusal names the item and the count, which is a better
      // message than a screen inventing its own.
      ...lineTracking(line),
    })),
  });

  const onSave = async () => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    try {
      if (isNew) {
        const created = await mutations.create.mutateAsync(requestBody());
        void navigate(`/inventory/adjustments/${created.id}`, { replace: true });
      } else {
        await mutations.update.mutateAsync(requestBody());
        loaded.current = null;
      }
    } catch (thrown) {
      handleFailure(thrown, isNew ? 'create' : 'edit');
    } finally {
      setBusy(false);
    }
  };

  const run = async (
    action: () => Promise<unknown>,
    permission: 'edit' | 'delete' | 'approve',
  ) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      loaded.current = null;
    } catch (thrown) {
      handleFailure(thrown, permission);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setDeleting(false);
    setBusy(true);
    setError(null);
    try {
      await mutations.remove.mutateAsync();
      void navigate('/inventory/adjustments');
    } catch (thrown) {
      handleFailure(thrown, 'delete');
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && loading) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  const approvalStatus = voucher?.approvalStatus ?? null;
  const approvalBlocks = requireApproval && approvalStatus !== 'approved';
  const rejected = requireApproval && approvalStatus === 'rejected';
  // The APPROVAL requirement is a rule, not a permission (16.7 row 3): a
  // voucher that needs sign-off keeps its Post button live-and-disabled with
  // the amber banner beside it saying so. `inventory.approve` is the separate,
  // greying question and no longer hides in here.
  const postDisabled = !isDraft || usedLines.length === 0 || approvalBlocks || busy || isNew;

  return (
    <div className="grid gap-4">
      {/* ── Toolbar ────────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t(isNew ? 'adj.newTitle' : 'adj.editTitle')}</h2>
        <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
          {voucher ? <Ltr value={voucher.code} /> : t('adj.codeNew')}
        </span>
        <VoucherStatusChip status={status} />
        {/* The toolbar's acts, DECLARED — see `document-toolbar.tsx`. */}
        <DocumentToolbar
          actions={[
            {
              // Not PART 4.6's Cancel row: a link back to the list wearing the
              // same word. The primitive's header records the collision.
              id: 'back',
              row: null,
              label: t('common.cancel'),
              variant: 'outline',
              to: '/inventory/adjustments',
            },
            {
              // The document's question and the role's, kept apart (16.7).
              id: 'delete',
              row: 'delete',
              label: t('adj.deleteDraft'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && isDraft,
              gate: { permission: 'inventory.delete', reasonKey: 'perm.cannot.deleteAdjustments' },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              // GEN-208: the click OPENS the question and does nothing else.
              // The write happens on the dialog's own verb, with GEN-59's
              // typed reason, never here.
              id: 'cancel-draft',
              row: 'cancel',
              label: t('adj.cancelDraft'),
              variant: 'outline',
              visible: !isNew && isDraft,
              gate: { permission: 'inventory.edit', reasonKey: 'perm.cannot.editAdjustments' },
              disabled: busy,
              onAct: () => setCancelAsk(true),
            },
            {
              id: 'save',
              row: 'save',
              label: t('common.save'),
              visible: isDraft,
              gate: {
                permission: isNew ? 'inventory.create' : 'inventory.edit',
                reasonKey: isNew
                  ? 'perm.cannot.createAdjustments'
                  : 'perm.cannot.editAdjustments',
              },
              busy,
              onAct: () => void onSave(),
            },
          ]}
        >
          {/* GEN-58 asks its sentence in the product’s ONE shared box
              (GEN-204, GEN-209 — "deleting a draft" is first on its list).
              `window.confirm` was neither of BR-PERM-15’s two patterns.
              Inside the same guard as the button, so the dialog cannot
              outlive the condition that allows the delete. */}
          {!isNew && isDraft ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('adj.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
          {/* Inside the same guard as the button, so the dialog cannot
              outlive the state that allows the cancel. */}
          {!isNew && isDraft ? (
            <ReasonBox
              open={cancelAsk}
              title={t('adj.confirmCancelTitle')}
              prompt={t('adj.confirmCancelBody')}
              reasonLabel={t('gen72.cancelReasonLabel')}
              confirmLabel={t('gen72.confirmCancel')}
              busy={busy}
              onConfirm={(reason) => {
                setCancelAsk(false);
                void run(() => mutations.cancel.mutateAsync(reason), 'edit');
              }}
              onCancel={() => setCancelAsk(false)}
            />
          ) : null}
        </DocumentToolbar>
      </div>

      {status === 'posted' ? (
        <p className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          {t('adj.postedNotice', { code: voucher?.code ?? '' })}
        </p>
      ) : null}
      {status === 'cancelled' ? (
        <p className="rounded-md border border-red-300 bg-red-50/80 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {/* GEN-59's stored reason, read back OFF THE RECORD. Rows cancelled
              before the reason existed keep the old sentence. */}
          {voucher?.cancelReason != null
            ? t('adj.cancelledReasonNotice', { reason: voucher.cancelReason })
            : t('adj.cancelledNotice')}
        </p>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}

      {/* ── The head ───────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="grid gap-4">
          <div className="grid gap-1.5">
            <span className="text-sm font-medium">{t('adj.fDirection')}</span>
            {/* Two buttons, not a dropdown: the direction is the document. */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={direction === 'in' ? 'default' : 'outline'}
                className={cn(direction === 'in' && 'bg-emerald-600 hover:bg-emerald-700')}
                disabled={!editable}
                onClick={() => onDirection('in')}
              >
                {t('adj.dirIn')}
              </Button>
              <Button
                type="button"
                variant={direction === 'out' ? 'default' : 'outline'}
                className={cn(direction === 'out' && 'bg-red-600 hover:bg-red-700')}
                disabled={!editable}
                onClick={() => onDirection('out')}
              >
                {t('adj.dirOut')}
              </Button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="grid gap-1.5">
              <Label htmlFor="adj-date">{t('common.date')}</Label>
              <Input
                id="adj-date"
                type="date"
                dir="ltr"
                value={adjustmentDate}
                disabled={!editable}
                onChange={(event) => setAdjustmentDate(event.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="adj-warehouse">{t('adj.fWarehouse')}</Label>
              <NativeSelect
                id="adj-warehouse"
                value={warehouseId}
                disabled={!editable}
                onChange={(event) => onWarehouse(event.target.value)}
              >
                <option value="">{t('adj.pickWarehouse')}</option>
                {(warehouses ?? []).map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            {/* Only where the location actually tracks bins (BR-INV-04-11). */}
            {showBins ? (
              <div className="grid gap-1.5">
                <Label htmlFor="adj-bin">{t('adj.fBin')}</Label>
                <NativeSelect
                  id="adj-bin"
                  value={binId}
                  disabled={!editable}
                  onChange={(event) => setBinId(event.target.value)}
                >
                  <option value="">{t('adj.binAny')}</option>
                  {(warehouse?.bins ?? []).map((bin) => (
                    <option key={bin.id} value={bin.id}>
                      {bin.zoneCode ? `${bin.code} · ${bin.zoneCode}` : bin.code}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            ) : null}
            <div className="grid gap-1.5">
              <Label htmlFor="adj-reason">{t('adj.fReason')}</Label>
              <NativeSelect
                id="adj-reason"
                value={reason}
                disabled={!editable}
                onChange={(event) => setReason(event.target.value)}
              >
                <option value="">{t('adj.pickReason')}</option>
                {reasons.map((option) => (
                  <option key={option} value={option}>
                    {t(`adj.reason.${option}`)}
                  </option>
                ))}
              </NativeSelect>
            </div>
            {/* PRJ-01 — WHERE the stock is going. Only an issue (Stock OUT)
                can be to a project, so the field exists only then; the
                `project_issue` reason requires it, every other OUT reason may
                carry it. Searched (GEN-50), with "no project" a legal row. */}
            {direction === 'out' ? (
              <div className="grid gap-1.5">
                <Label htmlFor="adj-project">{t('adj.fProject')}</Label>
                <Typeahead
                  id="adj-project"
                  label={t('adj.fProject')}
                  value={projectId === '' ? null : projectId}
                  onChange={(id) => setProjectId(id ?? '')}
                  options={projectPicker.options}
                  selected={projectPicker.optionFor(projectId === '' ? null : projectId)}
                  query={projectPicker.query}
                  onQueryChange={projectPicker.setQuery}
                  emptyLabel={t('picker.noMatchingProject')}
                  clearLabel={t('adj.noProject')}
                  loading={projectPicker.loading}
                  disabled={!editable}
                />
              </div>
            ) : null}
            <div className="grid gap-1.5">
              <Label htmlFor="adj-requested-by">{t('adj.fRequestedBy')}</Label>
              <Input
                id="adj-requested-by"
                value={requestedBy}
                disabled={!editable}
                onChange={(event) => setRequestedBy(event.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Approval (AP-6) ────────────────────────────────────────────── */}
      <details className="rounded-xl border" open={requireApproval}>
        <summary className="cursor-pointer px-4 py-3 text-sm font-semibold">
          {t('adj.secApproval')}
        </summary>
        <div className="grid gap-3 border-t p-4">
          <Label htmlFor="adj-require" className="flex items-center gap-2 font-normal">
            <Checkbox
              id="adj-require"
              checked={requireApproval}
              disabled={!editable}
              onCheckedChange={(checked) => setRequireApproval(checked === true)}
            />
            {t('adj.fRequireApproval')}
          </Label>
          {requireApproval ? (
            <div className="flex flex-wrap items-center gap-3">
              {approvalStatus ? <ApprovalChip status={approvalStatus} /> : null}
              {isDraft && approvalStatus === 'pending' ? (
                <>
                  <Gated permission="inventory.approve" reasonKey="perm.cannot.decideAdjustments">
                    <Button
                      size="sm"
                      onClick={() => void run(() => mutations.approve.mutateAsync('approved'), 'approve')}
                      disabled={busy}
                    >
                      {t('adj.approve')}
                    </Button>
                  </Gated>
                  <Gated permission="inventory.approve" reasonKey="perm.cannot.decideAdjustments">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void run(() => mutations.approve.mutateAsync('rejected'), 'approve')}
                      disabled={busy}
                    >
                      {t('adj.reject')}
                    </Button>
                  </Gated>
                </>
              ) : null}
              {voucher?.approvedByName ? (
                <span className="text-xs text-muted-foreground">
                  {voucher.approvedByName}
                  {voucher.approvedAt ? ` · ${voucher.approvedAt.slice(0, 10)}` : ''}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </details>

      {/* ── The lines ──────────────────────────────────────────────────── */}
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-11 text-center">#</TableHead>
                <TableHead>{t('adj.colItem')}</TableHead>
                <TableHead className="w-28 text-end">{t('adj.colQty')}</TableHead>
                <TableHead className="w-32 text-end">{t('adj.colUnitCost')}</TableHead>
                <TableHead className="w-32 text-end">{t('adj.colLineValue')}</TableHead>
                {editable ? <TableHead className="w-11" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line, index) => {
                const value = lineValue(line);
                const blocks = captureBlocks(itemsById.get(line.itemId), line.qty.trim());
                return (
                  <React.Fragment key={line.key}>
                  <TableRow className="odd:bg-muted/30">
                    <TableCell className="text-center font-mono text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <ItemCell
                        label={t('adj.cellItem', { line: index + 1 })}
                        clearLabel={t('adj.pickItem')}
                        picker={itemPicker}
                        value={line.itemId}
                        disabled={!editable}
                        onPick={(itemId) => {
                          const picked = itemPicker.byId.get(itemId);
                          setLine(line.key, {
                            itemId,
                            // BR-INV-08-17: the COST, never the selling price.
                            // Absent when the reader may not see costs (MD-03
                            // §9) — the server then fills the location's own.
                            ...(picked?.costPrice && line.unitCost.trim() === ''
                              ? { unitCost: picked.costPrice }
                              : {}),
                          });
                        }}
                      />
                    </TableCell>
                    {/*
                      * `N-4` — a POSTED voucher's quantity is a record, not
                      * something being typed. It sat in a merely DISABLED box
                      * printing "40" for a shelf measured in thousandths and
                      * naming no unit at all; `BR-INV-08-13` makes that box
                      * permanently un-typeable, so past Draft it is a display
                      * and reads as one.
                      */}
                    <TableCell className={editable ? undefined : 'text-end'}>
                      {editable ? (
                        <Input
                          aria-label={t('adj.cellQty', { line: index + 1 })}
                          dir="ltr"
                          inputMode="decimal"
                          className="h-9 text-end font-mono"
                          value={line.qty}
                          onChange={(event) => setLine(line.key, { qty: event.target.value })}
                        />
                      ) : line.qty.trim() === '' ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <Qty
                          value={line.qty}
                          precision={unitFor(line).precision}
                          unit={unitFor(line).symbol}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        aria-label={t('adj.cellUnitCost', { line: index + 1 })}
                        dir="ltr"
                        inputMode="decimal"
                        className="h-9 text-end font-mono"
                        value={line.unitCost}
                        disabled={!editable}
                        onChange={(event) => setLine(line.key, { unitCost: event.target.value })}
                      />
                    </TableCell>
                    <TableCell className="text-end">
                      {value === null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <Amount value={value} currency={baseCurrency} />
                      )}
                    </TableCell>
                    {editable ? (
                      <TableCell>
                        <button
                          type="button"
                          aria-label={t('sales.removeLine', { line: index + 1 })}
                          onClick={() =>
                            setLines((current) => {
                              const rest = current.filter((row) => row.key !== line.key);
                              return rest.length === 0 ? [blankLine()] : rest;
                            })
                          }
                          className="flex size-8 items-center justify-center rounded-md text-destructive transition-colors hover:bg-destructive/10"
                        >
                          <X className="size-4" aria-hidden />
                        </button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                  {/* OQ-05-17: the sub-table expands UNDER the line it
                      belongs to, one block per thing that has to be
                      identified — here always the line's own item. */}
                  <TrackingRows
                    blocks={blocks}
                    text={line.tracking}
                    onChange={(blockKey, value) => setTracking(line.key, blockKey, value)}
                    disabled={!editable}
                    columns={editable ? 6 : 5}
                    line={index + 1}
                  />
                  </React.Fragment>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={editable ? 4 : 3}>
                  {editable ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-dashed"
                      onClick={() => setLines((current) => [...current, blankLine()])}
                    >
                      <Plus aria-hidden /> {t('adj.addLine')}
                    </Button>
                  ) : null}
                </TableCell>
                <TableCell className="text-end">
                  <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    {t('adj.totals')}
                  </span>{' '}
                  <Amount value={totalValue} currency={baseCurrency} className="font-semibold" />
                </TableCell>
                {editable ? <TableCell /> : null}
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>

      {/* ── Status & posting ───────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t('adj.secPosting')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{t('adj.totalValue')}</span>
            <Amount
              value={voucher?.totalValue ?? totalValue}
              currency={baseCurrency}
              className="text-xl font-bold"
            />
            <DirectionChip direction={direction} />
          </div>
          {rejected ? (
            // BR-INV-08-08 — rejection blocks DISTINCTLY, not as "needs approval".
            <p className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
              <ShieldAlert className="size-4 shrink-0" aria-hidden />
              {localizeServerMessage(t, ADJUSTMENT_MESSAGES.rejected)}
            </p>
          ) : approvalBlocks ? (
            <p className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              <ShieldAlert className="size-4 shrink-0" aria-hidden />
              {localizeServerMessage(t, ADJUSTMENT_MESSAGES.approvalRequired)}
            </p>
          ) : null}
          {isDraft ? (
            <Gated
              permission="inventory.approve"
              reasonKey="perm.cannot.postAdjustments"
              className="justify-self-start"
            >
            <Button
              className={cn(
                'justify-self-start',
                direction === 'in'
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-blue-600 hover:bg-blue-700',
              )}
              disabled={postDisabled}
              onClick={() => void run(() => mutations.post.mutateAsync(), 'approve')}
            >
              {busy ? <Loader2 className="animate-spin" aria-hidden /> : null}
              {t(direction === 'in' ? 'adj.postIn' : 'adj.postOut')}
            </Button>
            </Gated>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
